import { Button } from "@/components/ui/button";
import { TEST_IDS } from "@/constants/testIds";
import { cn } from "@/lib/utils";
import { Square } from "lucide-react";
import PropTypes from "prop-types";
import { memo } from "react";

// Custom arrow up icon
const ArrowUpIcon = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
  >
    <path
      fillRule="evenodd"
      d="M11.293 2.293a1 1 0 0 1 1.414 0l7.5 7.5a1 1 0 1 1-1.414 1.414L13 5.414V21a1 1 0 0 1-2 0V5.414l-5.793 5.793a1 1 0 1 1-1.414-1.414l7.5-7.5Z"
      clipRule="evenodd"
    />
  </svg>
);

export const SendButton = memo(
  ({
    isUploading,
    disabled,
    className,
    onClick,
    isStreaming,
    onAbort,
    isEmpty = false,
  }) => {
    const handleClick = (e) => {
      e.preventDefault();

      if (isStreaming && onAbort) {
        // When streaming, clicking the button should abort the stream
        onAbort(e);
      } else if (!disabled && onClick) {
        // When not streaming and input has content, send the message
        onClick(e);
      }
    };

    return (
      <Button
        type="submit"
        size="sm"
        disabled={disabled || isUploading || (isEmpty && !isStreaming)} // Disable when empty (unless streaming), uploading, or explicitly disabled
        onClick={handleClick}
        data-testid={isStreaming ? TEST_IDS.CHAT_STOP_BUTTON : TEST_IDS.CHAT_SEND_BUTTON}
        className={cn(
          "h-9 w-9 p-1.5 rounded-full transition-colors",
          isStreaming
            ? "bg-white border border-gray-300 hover:bg-gray-50"
            : isEmpty
              ? "bg-gray-300 border border-gray-400 cursor-not-allowed"
              : "bg-gradient-to-br from-purple-400 via-purple-500 to-purple-700 hover:from-purple-500 hover:via-purple-600 hover:to-purple-800",
          "disabled:opacity-70 disabled:cursor-not-allowed",
          className
        )}
      >
        {isStreaming ? (
          <Square className="icon h-2 w-2 text-gray-600" />
        ) : (
          <ArrowUpIcon className="icon h-6 w-6 text-white" />
        )}
        <span className="sr-only">
          {isStreaming
            ? "Stop"
            : isEmpty
              ? "Enter text to send message"
              : isUploading
                ? "Uploading files..."
                : "Send message"}
        </span>
      </Button>
    );
  }
);

SendButton.displayName = "SendButton";

SendButton.propTypes = {
  isUploading: PropTypes.bool,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  onClick: PropTypes.func,
  isStreaming: PropTypes.bool,
  onAbort: PropTypes.func,
  isEmpty: PropTypes.bool,
};

export default SendButton;
